import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Tabs,
  Paper,
  Text,
  Button,
  Group,
  Stack,
  LoadingOverlay,
  Title,
  Avatar,
  Box,
  Modal,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconSettings,
  IconPackage,
  IconChartBar,
  IconDownload,
} from "@tabler/icons-react";
import { QRCodeCanvas } from "qrcode.react";

import ShopInventoryList from "./Inventory";
import ShopSettingsForm from "./components/ShopSettingsForm";
import ShopAnalyticsPanel from "../../components/ShopAnalyticsPanel";
import { API_BASE_URL } from "../../config/baseUrls";

const BASE_URL = `${API_BASE_URL}/shop-owner`;
const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("shopToken")}`,
});

// ==========================================
// UNIFIED ANALYTICS COMPONENT
// ==========================================
function UnifiedAnalyticsTab({ shopId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BASE_URL}/shops/${shopId}/analytics`, { headers: getHeaders() })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setStats(data.data);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [shopId]);

  return <ShopAnalyticsPanel stats={stats} loading={loading} />;
}

// ==========================================
// MAIN WORKSPACE COMPONENT (Exported)
// ==========================================
export default function ShopWorkspace() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("analytics");
  const [qrModalOpen, setQrModalOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${BASE_URL}/shops/${shopId}`, { headers: getHeaders() })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setShop(data.data);
        else navigate("/shop-owner/shops");
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [shopId, navigate]);

  const downloadQRCode = () => {
    const canvas = document.getElementById("shop-qr-code-main");
    if (!canvas) return;
    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    let downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `${shop.slug}-store-qr.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  if (loading) return <LoadingOverlay visible />;
  if (!shop) return <Text>Shop not found</Text>;

  const shopDeeplink = `${
    process.env.REACT_APP_SHOP_CUSTOMER_ORIGIN ||
    "https://ota-admin.altariumconsulting.com"
  }/shop/${shop.id}`;

  return (
    <Stack gap="xl" px={{ base: "xs", sm: "md" }}>
      {/* HEADER: Back Button, Shop Info, & Actions */}
      <Group align="center" wrap="wrap" gap="xl">
        <Group gap="md">
          <Button
            variant="subtle"
            color="gray"
            size="md"
            leftSection={<IconArrowLeft size={20} />}
            onClick={() => navigate("/shop-owner/shops")}
          >
            Back
          </Button>
          <Avatar src={shop.logoUrl} radius="md" size="xl" color="blue">
            {shop.name[0]}
          </Avatar>
          <Box>
            <Title order={2} size="h1">
              {shop.name}
            </Title>
            <Text c="dimmed" size="md">
              Manage your inventory and settings here.
            </Text>
          </Box>
        </Group>

        {/* The QR Code "Card" Button */}
        <Paper
          withBorder
          p="xs"
          radius="md"
          onClick={() => setQrModalOpen(true)}
          style={{ cursor: "pointer", transition: "all 0.2s ease" }}
          className="hover:shadow-md hover:border-blue-500"
          bg="gray.0"
        >
          <Group gap="sm" align="center">
            <Box
              style={{
                backgroundColor: "white",
                padding: "4px",
                borderRadius: "4px",
              }}
            >
              <QRCodeCanvas
                value={shopDeeplink}
                size={40}
                level={"L"}
                includeMargin={false}
              />
            </Box>
            <Box pr="sm">
              <Text fw={700} size="md">
                Store QR Code
              </Text>
              <Text size="sm" c="dimmed">
                Click to view & print
              </Text>
            </Box>
          </Group>
        </Paper>
      </Group>

      {/* WORKSPACE TABS */}
      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        radius="md"
        variant="pills"
        color="dark"
      >
        <Tabs.List>
          <Tabs.Tab value="analytics" leftSection={<IconChartBar size={18} />}>
            <Text size="md" fw={600}>
              Analytics
            </Text>
          </Tabs.Tab>
          <Tabs.Tab value="products" leftSection={<IconPackage size={18} />}>
            <Text size="md" fw={600}>
              Inventory
            </Text>
          </Tabs.Tab>
          <Tabs.Tab value="settings" leftSection={<IconSettings size={18} />}>
            <Text size="md" fw={600}>
              Settings
            </Text>
          </Tabs.Tab>
        </Tabs.List>

        <Paper
          p={{ base: "md", sm: "xl" }}
          withBorder
          radius="lg"
          mt="lg"
          mih={500}
          shadow="sm"
        >
          <Tabs.Panel value="analytics">
            <UnifiedAnalyticsTab shopId={shopId} />
          </Tabs.Panel>
          <Tabs.Panel value="products">
            <ShopInventoryList shopId={shopId} />
          </Tabs.Panel>
          <Tabs.Panel value="settings">
            <ShopSettingsForm
              shop={shop}
              onUpdate={() => window.location.reload()}
            />
          </Tabs.Panel>
        </Paper>
      </Tabs>

      {/* Sleek QR Code Modal */}
      <Modal
        opened={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        title={<Title order={3}>Scan to Order</Title>}
        centered
        radius="lg"
        size="md"
      >
        <Stack align="center" gap="lg" p="md">
          <Text size="md" c="dimmed" ta="center">
            Print this QR code and place it at your booth. Customers can scan it
            to view your menu and order instantly.
          </Text>
          <Paper withBorder p="xl" radius="lg" shadow="sm">
            <QRCodeCanvas
              id="shop-qr-code-main"
              value={shopDeeplink}
              size={240}
              level={"H"}
              includeMargin={true}
              imageSettings={
                shop.logoUrl
                  ? {
                      src: shop.logoUrl,
                      x: undefined,
                      y: undefined,
                      height: 50,
                      width: 50,
                      excavate: true,
                      crossOrigin: "anonymous",
                    }
                  : undefined
              }
            />
          </Paper>
          <Button
            fullWidth
            size="xl"
            color="dark"
            mt="md"
            leftSection={<IconDownload size={20} />}
            onClick={downloadQRCode}
          >
            Download High-Res PNG
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
